/**
 * Logging Configuration Utility
 * 
 * This utility allows users to configure logging levels at runtime
 * for debugging purposes without modifying code.
 */

import logger, { LogLevel, LogCategory } from './logger';

export class LoggingConfig {
  private static instance: LoggingConfig;
  private configPanel: HTMLElement | null = null;

  public static getInstance(): LoggingConfig {
    if (!LoggingConfig.instance) {
      LoggingConfig.instance = new LoggingConfig();
    }
    return LoggingConfig.instance;
  }

  /**
   * Show the logging configuration panel
   */
  public showConfigPanel(): void {
    if (this.configPanel) {
      this.configPanel.remove();
    }

    this.configPanel = this.createConfigPanel();
    document.body.appendChild(this.configPanel);
  }

  /**
   * Hide the logging configuration panel
   */
  public hideConfigPanel(): void {
    if (this.configPanel) {
      this.configPanel.remove();
      this.configPanel = null;
    }
  }

  /**
   * Set logging level
   */
  public setLogLevel(level: LogLevel): void {
    logger.setLevel(level);
    this.saveConfig();
  }

  /**
   * Enable/disable specific log categories
   */
  public setCategoryEnabled(category: LogCategory, enabled: boolean): void {
    if (enabled) {
      logger.enableCategory(category);
    } else {
      logger.disableCategory(category);
    }
    this.saveConfig();
  }

  /**
   * Enable/disable timestamps
   */
  public setTimestampsEnabled(enabled: boolean): void {
    logger.setTimestamps(enabled);
    this.saveConfig();
  }

  /**
   * Enable/disable emojis
   */
  public setEmojisEnabled(enabled: boolean): void {
    logger.setEmojis(enabled);
    this.saveConfig();
  }

  /**
   * Reset to default configuration
   */
  public resetToDefaults(): void {
    logger.setLevel(LogLevel.WARN);
    logger.setTimestamps(true);
    logger.setEmojis(true);
    
    // Enable all categories
    Object.values(LogCategory).forEach(category => {
      logger.enableCategory(category);
    });
    
    this.saveConfig();
    this.updateConfigPanel();
  }

  /**
   * Load configuration from localStorage
   */
  public loadConfig(): void {
    try {
      const saved = localStorage.getItem('logging-config');
      if (saved) {
        const config = JSON.parse(saved);
        
        if (config.level !== undefined) {
          logger.setLevel(config.level);
        }
        
        if (config.timestamps !== undefined) {
          logger.setTimestamps(config.timestamps);
        }
        
        if (config.emojis !== undefined) {
          logger.setEmojis(config.emojis);
        }
        
        if (config.categories) {
          Object.entries(config.categories).forEach(([category, enabled]) => {
            if (enabled) {
              logger.enableCategory(category as LogCategory);
            } else {
              logger.disableCategory(category as LogCategory);
            }
          });
        }
      }
    } catch (error) {
      console.warn('Failed to load logging configuration:', error);
    }
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    try {
      const config = {
        level: logger['config'].level,
        timestamps: logger['config'].enableTimestamps,
        emojis: logger['config'].enableEmojis,
        categories: Object.values(LogCategory).reduce((acc, category) => {
          acc[category] = logger['config'].categories.has(category);
          return acc;
        }, {} as Record<string, boolean>)
      };
      
      localStorage.setItem('logging-config', JSON.stringify(config));
    } catch (error) {
      console.warn('Failed to save logging configuration:', error);
    }
  }

  /**
   * Create the configuration panel UI
   */
  private createConfigPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 300px;
      background: white;
      border: 1px solid #ccc;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: Arial, sans-serif;
      font-size: 14px;
    `;

    panel.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
        <h3 style="margin: 0; font-size: 16px;">Logging Configuration</h3>
        <button id="close-panel" style="background: none; border: none; font-size: 18px; cursor: pointer;">×</button>
      </div>
      
      <div style="margin-bottom: 15px;">
        <label style="display: block; margin-bottom: 5px; font-weight: bold;">Log Level:</label>
        <select id="log-level" style="width: 100%; padding: 5px;">
          <option value="0">ERROR</option>
          <option value="1">WARN</option>
          <option value="2">INFO</option>
          <option value="3">DEBUG</option>
        </select>
      </div>
      
      <div style="margin-bottom: 15px;">
        <label style="display: block; margin-bottom: 5px; font-weight: bold;">Categories:</label>
        <div id="categories" style="max-height: 150px; overflow-y: auto;"></div>
      </div>
      
      <div style="margin-bottom: 15px;">
        <label style="display: flex; align-items: center;">
          <input type="checkbox" id="timestamps" style="margin-right: 8px;">
          Enable Timestamps
        </label>
      </div>
      
      <div style="margin-bottom: 15px;">
        <label style="display: flex; align-items: center;">
          <input type="checkbox" id="emojis" style="margin-right: 8px;">
          Enable Emojis
        </label>
      </div>
      
      <div style="display: flex; gap: 10px;">
        <button id="reset-config" style="flex: 1; padding: 8px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 4px; cursor: pointer;">Reset</button>
        <button id="test-log" style="flex: 1; padding: 8px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">Test Log</button>
      </div>
    `;

    this.setupEventListeners(panel);
    this.updateConfigPanel();
    
    return panel;
  }

  /**
   * Setup event listeners for the config panel
   */
  private setupEventListeners(panel: HTMLElement): void {
    // Close button
    panel.querySelector('#close-panel')?.addEventListener('click', () => {
      this.hideConfigPanel();
    });

    // Log level
    panel.querySelector('#log-level')?.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      this.setLogLevel(parseInt(target.value) as LogLevel);
    });

    // Timestamps
    panel.querySelector('#timestamps')?.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      this.setTimestampsEnabled(target.checked);
    });

    // Emojis
    panel.querySelector('#emojis')?.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      this.setEmojisEnabled(target.checked);
    });

    // Reset button
    panel.querySelector('#reset-config')?.addEventListener('click', () => {
      this.resetToDefaults();
    });

    // Test log button
    panel.querySelector('#test-log')?.addEventListener('click', () => {
      this.testLogging();
    });
  }

  /**
   * Update the config panel with current values
   */
  private updateConfigPanel(): void {
    if (!this.configPanel) return;

    // Update log level
    const logLevelSelect = this.configPanel.querySelector('#log-level') as HTMLSelectElement;
    if (logLevelSelect) {
      logLevelSelect.value = logger['config'].level.toString();
    }

    // Update categories
    const categoriesDiv = this.configPanel.querySelector('#categories');
    if (categoriesDiv) {
      categoriesDiv.innerHTML = '';
      Object.values(LogCategory).forEach(category => {
        const isEnabled = logger['config'].categories.has(category);
        const label = document.createElement('label');
        label.style.cssText = 'display: flex; align-items: center; margin-bottom: 5px;';
        label.innerHTML = `
          <input type="checkbox" ${isEnabled ? 'checked' : ''} style="margin-right: 8px;">
          ${category}
        `;
        
        const checkbox = label.querySelector('input') as HTMLInputElement;
        checkbox.addEventListener('change', () => {
          this.setCategoryEnabled(category, checkbox.checked);
        });
        
        categoriesDiv.appendChild(label);
      });
    }

    // Update timestamps
    const timestampsCheckbox = this.configPanel.querySelector('#timestamps') as HTMLInputElement;
    if (timestampsCheckbox) {
      timestampsCheckbox.checked = logger['config'].enableTimestamps;
    }

    // Update emojis
    const emojisCheckbox = this.configPanel.querySelector('#emojis') as HTMLInputElement;
    if (emojisCheckbox) {
      emojisCheckbox.checked = logger['config'].enableEmojis;
    }
  }

  /**
   * Test logging at different levels
   */
  private testLogging(): void {
    logger.error(LogCategory.GENERAL, 'This is a test ERROR message');
    logger.warn(LogCategory.GENERAL, 'This is a test WARN message');
    logger.info(LogCategory.GENERAL, 'This is a test INFO message');
    logger.debug(LogCategory.GENERAL, 'This is a test DEBUG message');
  }
}

// Export singleton instance
export const loggingConfig = LoggingConfig.getInstance();

// Make it available globally for easy access
(window as any).loggingConfig = loggingConfig;

// Auto-load configuration on import
loggingConfig.loadConfig();
